/* eslint-disable no-unused-expressions */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Row, Col, Space, Spin } from 'antd';

import { Button, ErrorMessage } from '@atoms';
import { LocaleSelect } from '@molecules';
import RichTextField from '@molecules/RichTextField';
import { TOOLBAR_ITEM } from '@atoms/RichTextEditor/constants';
import Form from '@organisms/Form';
import { DEFAULT_LOCALE } from '@constants';
import { useRule, usePublishRule, useUpsertRule } from '@api/hooks';
import { isAnyQueryFetching, getAnyQueryError, withOnSuccess } from '@utils/query';
import { useVersionedEntity } from '@hooks';

import { FORM_FIELD } from './constants';

const RuleForm = ({ parentSectionId, ruleId, onSuccess, onClose, onCancel = onClose, editing, locale: propLocale }) => {
  const [form] = Form.useForm(FORM_FIELD);
  const [locale, setLocale] = React.useState(propLocale || DEFAULT_LOCALE);

  const { rule: defaultLocaleRule, ...defaultLocaleRuleQuery } = useRule({ id: ruleId });
  const { data: rule, ...ruleQuery } = useRule({ id: ruleId, locale });

  const initialValues = React.useMemo(() => form.deserialize(rule || defaultLocaleRule), [
    form,
    rule,
    defaultLocaleRule,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const allQueries = [ruleQuery, defaultLocaleRuleQuery];
  const isFetching = isAnyQueryFetching(allQueries);
  const queryError = getAnyQueryError(
    allQueries.filter((query) => {
      // ignore 404 errors of other language
      if (query === ruleQuery && query.error?.response?.status === 404) return false;
      return true;
    }),
  );

  const formName = React.useMemo(() => `rule-form-${Math.random()}`, []);

  const { isPublishing, isSavingDraft, error, ...entity } = useVersionedEntity({
    useSaveDraft: useUpsertRule,
    usePublish: usePublishRule,
    getFormValues: form.remoteSubmit,
    onError: form.setApiErrors,
    doRedirect: false,
  });

  const noEnglishVersion = locale === 'fr' && !defaultLocaleRule?.published;

  React.useEffect(() => {
    form.resetFields();
    form.setFieldsValue(initialValues);
  }, [form, initialValues, locale]); // eslint-disable-line react-hooks/exhaustive-deps

  // reset any error after previous submit when changing language
  React.useEffect(() => {
    entity.reset();
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  const withHandleOnSuccess = (config) =>
    withOnSuccess(config, (data) => {
      onSuccess?.(data);
      if (!ruleId) form.resetFields();
      /**
       * If we have changed english language, then switched to other language and saved draft in modal
       * Then switched back to english - we will have old data because query is always active in (defaultLocaleRule)
       * So we force refetch when locale changes and it's default language
       */
      if (ruleId && data?.language === DEFAULT_LOCALE) {
        defaultLocaleRuleQuery.refetch();
      }
    });

  const commonProps = { id: ruleId, locale, parentSectionId };

  const handlePublish = (props, config) =>
    entity.handlePublish({ ...props, ...commonProps }, withHandleOnSuccess(config));
  const handleSaveDraft = (props, config) =>
    entity.handleSaveDraft({ ...props, ...commonProps }, withHandleOnSuccess(config));

  return (
    <Spin spinning={isFetching}>
      <Form form={form} name={formName} initialValues={initialValues} layout="vertical" component="div">
        <ErrorMessage error={[queryError, error]} variant="block" spaced concatenateApiErrors />
        <LocaleSelect
          locale={locale}
          disabled={!ruleId}
          onChange={setLocale}
          needsSave={form.isFieldsTouched}
          // throw error so LocaleSelect can catch error and prevent changing language
          onSaveDraft={() => handleSaveDraft(null, { throwOnError: true })}
          onPublish={() => handlePublish(null, { throwOnError: true })}
        />
        <RichTextField {...FORM_FIELD.TEXT} include={[TOOLBAR_ITEM.DELIMITER, TOOLBAR_ITEM.TOOLTIP]} />
        {noEnglishVersion && (
          <Row style={{ marginBottom: 30 }}>
            You can&apos;t publish this language version until English is published
          </Row>
        )}
        <Row justify="space-between" gutter="15">
          <Col>
            <Space size={15}>
              <Button
                loading={isPublishing}
                onClick={() => handlePublish(null, { onSuccess: onClose })}
                type="customBlue"
                disabled={noEnglishVersion}
              >{`${rule?.published ? 'UPDATE' : 'PUBLISH'} RULE`}</Button>
              <Button
                loading={isSavingDraft}
                onClick={() => handleSaveDraft(null, { onSuccess: onClose })}
                disabled={noEnglishVersion}
              >
                SAVE DRAFT
              </Button>
            </Space>
          </Col>
          {editing && (
            <Col>
              <Space size={15}>
                <Button onClick={onCancel}>CANCEL</Button>
              </Space>
            </Col>
          )}
        </Row>
      </Form>
    </Spin>
  );
};

export default RuleForm;
